import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import React from 'react';
import { WhatsAppNotificationCard } from '@/components/whatsapp-inbox/WhatsAppNotificationCard';

/**
 * useNotificacaoDuvidaSupervisora — avisa a supervisora da Qualidade na hora
 * que um cliente aperta "Tenho dúvida" no template de fatura.
 *
 * Mesma mecânica do useNotificacaoQualidadeResposta (card persistente +
 * som + notificação nativa), mas com:
 *   - Variant 'duvida' (roxo) pra distinguir do verde de BV/Fatura
 *   - Filtro: só notifica o líder da equipe QUALIDADE (Lisiane hoje)
 *   - Detecta UPDATE em qualidade_ciclo onde fatura_situacao virou 'duvida'
 *   - Botão CTA = "Atender dúvida agora" (navega pra conversa)
 *
 * Não confunde com o card verde existente — esse é um canal próprio pra ela
 * ver a fila de dúvidas em tempo real e atender ANTES de virar nota 1.
 */
export function useNotificacaoDuvidaSupervisora() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const ativosRef = useRef<Map<string, string | number>>(new Map());
  const isLiderRef = useRef<boolean>(false);

  // Pré-carrega áudio
  useEffect(() => {
    audioRef.current = new Audio('/notification-whatsapp.mp3');
    audioRef.current.volume = 0.6;
  }, []);

  // Detecta se o usuário logado é o líder da equipe QUALIDADE.
  // Mantém-se correto se a liderança mudar (Lisiane sair, outro entrar).
  useEffect(() => {
    if (!profile?.id) {
      isLiderRef.current = false;
      return;
    }
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('equipes')
        .select('lider_id')
        .eq('nome', 'QUALIDADE')
        .eq('ativo', true)
        .limit(1)
        .maybeSingle();
      if (!cancelled) {
        isLiderRef.current = (data as any)?.lider_id === profile.id;
      }
    })();
    return () => { cancelled = true; };
  }, [profile?.id]);

  const tocar = () => {
    try {
      audioRef.current && (audioRef.current.currentTime = 0, audioRef.current.play().catch(() => {}));
    } catch {}
  };

  const showCard = (
    chave: string,
    nome: string,
    onAbrir: () => void,
  ) => {
    const existente = ativosRef.current.get(chave);
    if (existente !== undefined) toast.dismiss(existente);
    tocar();
    const id = toast.custom(
      (t) =>
        React.createElement(WhatsAppNotificationCard, {
          variant: 'duvida',
          titulo: '❓ Cliente está com dúvida',
          nome,
          preview: 'Atenda no WhatsApp e marque o resultado (Resolvida / Perdida / Anotar) — vai pra timeline do cliente.',
          badge: 'DÚVIDA',
          ctaLabel: 'Atender dúvida agora',
          onAbrir: () => {
            toast.dismiss(t);
            ativosRef.current.delete(chave);
            onAbrir();
          },
          onClose: () => {
            toast.dismiss(t);
            ativosRef.current.delete(chave);
          },
        }),
      { duration: Infinity, position: 'top-right' },
    );
    ativosRef.current.set(chave, id);

    // Notificação nativa do navegador (tab em background)
    if (typeof window !== 'undefined' && 'Notification' in window) {
      try {
        if (Notification.permission === 'default') Notification.requestPermission().catch(() => {});
        if (Notification.permission === 'granted' && document.hidden) {
          const native = new Notification('❓ Cliente em dúvida', {
            body: `${nome} apertou "Tenho dúvida" — atenda agora`,
            icon: '/favicon.ico',
            tag: chave,
            requireInteraction: true,
          });
          native.onclick = () => {
            window.focus();
            toast.dismiss(id);
            ativosRef.current.delete(chave);
            onAbrir();
            native.close();
          };
        }
      } catch {}
    }
  };

  const acharConversa = async (clienteId: string | null): Promise<string | null> => {
    if (!clienteId) return null;
    const { data: cli } = await supabase
      .from('clientes')
      .select('whatsapp, telefone_principal')
      .eq('id', clienteId)
      .maybeSingle();
    const raw = (cli as any)?.whatsapp || (cli as any)?.telefone_principal || '';
    const dig = String(raw).replace(/\D/g, '');
    if (dig.length < 10) return null;
    const sufixo = dig.slice(-11);
    // Conversa da Lisiane (ela é dona após transferência automática)
    const { data: convs } = await supabase
      .from('whatsapp_conversations')
      .select('id, wa_id')
      .eq('owner_user_id', profile?.id || '');
    for (const c of convs || []) {
      const wa = String((c as any).wa_id || '').replace(/\D/g, '');
      if (wa.endsWith(sufixo)) return (c as any).id;
    }
    return null;
  };

  const nomeCliente = async (clienteId: string | null) => {
    if (!clienteId) return 'Cliente';
    const { data } = await supabase
      .from('clientes')
      .select('razao_social, nome_fantasia')
      .eq('id', clienteId)
      .maybeSingle();
    return (data as any)?.nome_fantasia || (data as any)?.razao_social || 'Cliente';
  };

  // Subscribe realtime — só processa eventos se o usuário for líder da Qualidade.
  // (verificação assíncrona — usa ref pra não recriar subscription)
  useEffect(() => {
    if (!profile?.id) return;

    const ch = supabase
      .channel('duvida-supervisora-realtime')
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'qualidade_ciclo' },
        async (payload) => {
          if (!isLiderRef.current) return; // só Lisiane

          const nv = payload.new as any;
          const old = payload.old as any;

          // Só dispara quando fatura_situacao MUDA pra 'duvida'
          // (ignora updates de outros campos em ciclos já em dúvida)
          const virouDuvida = nv?.fatura_situacao === 'duvida' && old?.fatura_situacao !== 'duvida';
          if (!virouDuvida) return;

          const nome = await nomeCliente(nv?.cliente_id || null);
          const convId = await acharConversa(nv?.cliente_id || null);

          qc.invalidateQueries({ queryKey: ['duvidas-supervisora'] });
          qc.invalidateQueries({ queryKey: ['qualidade-ciclos'] });

          showCard(
            `duvida-${nv.id}`,
            nome,
            () => navigate(convId ? `/whatsapp-inbox?conversation=${convId}` : '/whatsapp-inbox'),
          );
        },
      )
      .subscribe();

    return () => { ch.unsubscribe(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.id]);
}
